import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import {
  ArrowRight,
  Shield,
  Cpu,
  Network,
  Layers,
  RefreshCw,
  Server,
  Zap,
  MessageSquare,
} from 'lucide-react'

export const Route = createFileRoute('/_site/blog/why-beam')({
  component: WhyBeamPage,
  head: () => ({
    meta: [
      {
        title:
          'Why We Chose BEAM for Uptime Monitoring — Uptrack',
      },
      {
        name: 'description',
        content:
          'Archived, written for the Elixir version: why we first built Uptrack on the BEAM VM. Since June 2026 the backend is Rust.',
      },
      {
        property: 'og:title',
        content: 'Why We Chose BEAM for Uptime Monitoring — Uptrack',
      },
      {
        property: 'og:description',
        content:
          'The Erlang VM was built for telecom switches that need 99.9999% uptime. We chose it to build a monitoring tool that never goes down.',
      },
      {
        property: 'og:image',
        content: 'https://uptrack.app/og/blog-why-beam.png',
      },
      { property: 'og:type', content: 'article' },
      {
        property: 'og:url',
        content: 'https://uptrack.app/blog/why-beam',
      },
      { property: 'article:published_time', content: '2026-04-01' },
    ],
    links: [
      {
        rel: 'canonical',
        href: 'https://uptrack.app/blog/why-beam',
      },
    ],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: 'Why We Chose BEAM for Uptime Monitoring',
          description:
            'The Erlang VM was built for telecom switches that need 99.9999% uptime. We chose it to build a monitoring tool that never goes down.',
          datePublished: '2026-04-01',
          dateModified: '2026-05-22',
          author: { '@type': 'Organization', name: 'Uptrack' },
          publisher: {
            '@type': 'Organization',
            name: 'Uptrack',
            url: 'https://uptrack.app',
          },
          mainEntityOfPage:
            'https://uptrack.app/blog/why-beam',
        }),
      },
    ],
  }),
})

function WhyBeamPage() {
  return (
    <>

      <>
        <article className="article-prose container mx-auto max-w-2xl px-4 py-16">
          <p className="mb-6 rounded-lg border border-border bg-muted p-4 text-lg leading-[1.7] text-body">
            Archived: written when Uptrack ran on Elixir. Since June 2026 the backend is Rust.
          </p>
          <p className="mb-3 text-sm text-muted-foreground">April 1, 2026</p>
          <h1 className="font-sans mb-6 text-4xl font-semibold">
            Why we chose BEAM for uptime monitoring
          </h1>

          {/* Hook */}
          <section className="mb-12">
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              When your monitoring tool goes down, who monitors the monitor?
            </p>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              This isn't a philosophical riddle. It's the most practical
              question you can ask when choosing a tech stack for monitoring
              infrastructure. Your monitoring tool is the last line of defense.
              If it crashes at 3 AM, nobody knows your production app is down
              until customers start tweeting about it.
            </p>
            <p className="text-lg leading-[1.7] text-muted-foreground">
              We chose a runtime that was designed to never go down. Not
              "designed to be fast" or "designed to be easy to hire for." Designed
              to keep running when everything around it is failing. That runtime
              is the BEAM VM, and it's the foundation of Uptrack.
            </p>
          </section>

          {/* What is BEAM */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              What is the BEAM VM?
            </h2>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              BEAM is the virtual machine that runs Erlang and Elixir. It was
              created at Ericsson in the late 1980s for telecom switching
              systems -- the kind of software that routes your phone calls and
              needs 99.9999% uptime (that's about 32 seconds of downtime per
              year).
            </p>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              Telecom engineers had a constraint most web developers never face:
              you cannot restart the system. Ever. Millions of active phone calls
              depend on it. So they built a runtime where individual components
              can crash and restart without affecting the rest of the system.
              Where you can swap out code while the system is running. Where
              millions of lightweight concurrent processes communicate through
              message passing instead of sharing memory.
            </p>
            <p className="text-lg leading-[1.7] text-muted-foreground">
              Today, the BEAM powers WhatsApp (2 billion users, ~50 engineers),
              Discord (200 million users), RabbitMQ, and most telecom
              infrastructure worldwide. And now it powers Uptrack.
            </p>
          </section>

          {/* Why BEAM for monitoring */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              Why BEAM is uniquely suited for monitoring
            </h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              Every language can make HTTP requests and check status codes. The
              difference is what happens when things go wrong -- and in
              monitoring, things go wrong constantly. Targets time out. DNS
              lookups fail. Database connections drop. The question isn't whether
              failures happen, it's whether one failure cascades into many.
            </p>

            <div className="space-y-6">
              <div className="flex gap-4">
                <Shield className="mt-1 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <h3 className="font-sans mb-1 font-semibold">
                    Fault tolerance through supervisor trees
                  </h3>
                  <p className="text-lg leading-[1.7] text-body">
                    In most runtimes, an unhandled exception crashes the process.
                    In BEAM, each "process" is a tiny isolated unit (not an OS
                    process -- more like a goroutine, but with its own heap and
                    mailbox). When a BEAM process crashes, its supervisor
                    restarts it automatically. The rest of the system doesn't
                    notice. If a health check for one monitor hits a malformed
                    response and the parsing code crashes, only that single check
                    restarts. The other 10,000 concurrent checks continue
                    uninterrupted. This isn't something we had to build. It's how
                    the runtime works.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <RefreshCw className="mt-1 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <h3 className="font-sans mb-1 font-semibold">
                    Hot code upgrades: deploy without downtime
                  </h3>
                  <p className="text-lg leading-[1.7] text-body">
                    BEAM can load new code modules while the system is running.
                    This was built for telecom switches that couldn't be
                    restarted, and it means we can ship fixes and features
                    without dropping a single health check. No rolling restart
                    window where checks are paused. No brief gap where an outage
                    could go undetected. The old code handles in-flight requests
                    while new requests hit the new code.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <Layers className="mt-1 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <h3 className="font-sans mb-1 font-semibold">
                    Lightweight processes for massive concurrency
                  </h3>
                  <p className="text-lg leading-[1.7] text-body">
                    A BEAM process uses about 2KB of memory. You can run
                    hundreds of thousands of them on a single machine. Each
                    monitor check runs in its own process with its own timeout,
                    its own error handling, and its own lifecycle. This means
                    100,000 concurrent checks don't require 100,000 OS threads
                    or a complex async/await state machine. They're just
                    processes doing their thing, scheduled preemptively by the
                    BEAM across all available CPU cores.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <Network className="mt-1 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <h3 className="font-sans mb-1 font-semibold">
                    Built-in distribution for clustering
                  </h3>
                  <p className="text-lg leading-[1.7] text-body">
                    BEAM nodes can form clusters natively. Processes on different
                    machines communicate with the same message-passing syntax as
                    processes on the same machine. If one node goes down, the
                    other nodes detect it within seconds and can take over its
                    work. We didn't need to add Kubernetes, service mesh, or a
                    separate consensus protocol. Distribution is a language-level
                    primitive.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <MessageSquare className="mt-1 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <h3 className="font-sans mb-1 font-semibold">
                    Message passing eliminates shared state bugs
                  </h3>
                  <p className="text-lg leading-[1.7] text-body">
                    BEAM processes don't share memory. They communicate by
                    sending immutable messages. No mutexes, no locks, no race
                    conditions, no "this works fine until you add a second
                    thread." When you're running thousands of concurrent health
                    checks that all need to write results, update incident
                    status, and trigger alerts, the absence of shared-state bugs
                    isn't a nice-to-have. It's the difference between a system
                    that works under load and one that works "most of the time."
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Our architecture */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              How we put it together
            </h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              Theory is nice. Here's what Uptrack actually looks like in
              production.
            </p>

            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-card p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Server className="h-5 w-5 text-primary" />
                  <h3 className="font-sans font-semibold">Dual API nodes on Netcup Germany</h3>
                </div>
                <p className="text-lg leading-[1.7] text-body">
                  Two independent Elixir/Phoenix nodes, each capable of handling
                  the full workload. An outbound tunnel runs on both nodes with
                  the same tunnel token. If one node dies, the tunnel
                  automatically routes traffic to the surviving node within ~5
                  seconds. No load balancer to configure or maintain.
                </p>
              </div>

              <div className="rounded-lg border border-border bg-card p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Cpu className="h-5 w-5 text-primary" />
                  <h3 className="font-sans font-semibold">Patroni PostgreSQL with automatic failover</h3>
                </div>
                <p className="text-lg leading-[1.7] text-body">
                  PostgreSQL runs in a Patroni cluster with HAProxy routing
                  queries to the current primary. If the primary goes down,
                  Patroni promotes the replica and HAProxy redirects
                  automatically. Our application connects to a stable local
                  address that never changes, regardless of which node is
                  primary.
                </p>
              </div>

              <div className="rounded-lg border border-border bg-card p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="h-5 w-5 text-primary" />
                  <h3 className="font-sans font-semibold">Oban for reliable job processing</h3>
                </div>
                <p className="text-lg leading-[1.7] text-body">
                  Health checks, alert delivery, and incident management run as
                  Oban jobs backed by PostgreSQL. If a check fails mid-execution
                  (node crash, OOM kill), Oban automatically retries it on the
                  surviving node. No Redis dependency. No separate job queue
                  to monitor and maintain. The database is the queue, and
                  PostgreSQL is extremely good at being reliable.
                </p>
              </div>
            </div>

            <p className="mt-6 text-lg leading-[1.7] text-muted-foreground">
              The entire stack is designed so that any single component can fail
              without the system going down. The BEAM handles application-level
              failures. Patroni handles database failover. The tunnel handles
              network-level failover. Each layer is independent and self-healing.
            </p>
          </section>

          {/* What we considered */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              What we considered instead
            </h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              We didn't pick Elixir/BEAM because it was trendy. We evaluated
              serious alternatives and each fell short for this specific use case.
            </p>

            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-card p-5">
                <h3 className="font-sans mb-2 font-semibold">Go</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Go is fast, compiles to a single binary, and has excellent
                  concurrency with goroutines. But goroutines don't have
                  supervisors. A panic in one goroutine can crash the entire
                  process if you're not extremely careful with recovery. Go has
                  no built-in distribution, no hot code upgrades, and error
                  handling is explicit but tedious. For a monitoring service where
                  you need thousands of independent, self-healing tasks, you'd
                  end up reimplementing half of what BEAM gives you for free.
                </p>
              </div>

              <div className="rounded-lg border border-border bg-card p-5">
                <h3 className="font-sans mb-2 font-semibold">Node.js</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Node.js is single-threaded. You can work around this with
                  worker threads or cluster mode, but you're fighting the
                  runtime's fundamental design. An unhandled promise rejection
                  can crash the process. There's no built-in fault isolation
                  between concurrent operations. For a monitoring tool that needs
                  to run 100K concurrent checks with independent failure domains,
                  Node.js requires bolting on infrastructure (PM2, Redis,
                  external job queues) to approximate what BEAM provides natively.
                </p>
              </div>

              <div className="rounded-lg border border-border bg-card p-5">
                <h3 className="font-sans mb-2 font-semibold">Rust</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Rust is an extraordinary language for systems programming.
                  Memory safety without a garbage collector is genuinely
                  impressive. But for a web service that makes HTTP requests and
                  writes to a database, Rust's compile times and borrow checker
                  are friction without proportional benefit. We don't need
                  zero-cost abstractions or manual memory management. We need
                  fault tolerance, concurrency, and rapid iteration. Rust is a
                  great choice for building a database. It's overkill for
                  building a web application on top of one.
                </p>
              </div>
            </div>
          </section>

          {/* The tradeoff */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              The honest tradeoff
            </h2>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              We're not going to pretend BEAM has no downsides. The Elixir
              talent pool is smaller than JavaScript or Python. You won't find
              Elixir developers on every street corner. The ecosystem, while
              mature, has fewer libraries than Node.js or Go. Raw computational
              throughput is lower than Go or Rust (though for I/O-bound work
              like HTTP monitoring, this is irrelevant).
            </p>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              But here's the thing: we're not building a generic web app. We're
              building monitoring infrastructure. The tool that needs to be
              running when everything else is broken. The tool that checks your
              servers at 3 AM, detects the outage, and wakes you up. For that
              specific job, the BEAM's tradeoffs are exactly right.
            </p>
            <p className="text-lg leading-[1.7] text-muted-foreground">
              Fewer developers who know the language, but the ones who do
              understand distributed systems deeply. Fewer libraries, but the
              standard library covers concurrency, networking, and fault
              tolerance out of the box. Slower raw computation, but the ability
              to handle massive concurrent I/O with processes that self-heal
              when they fail. For monitoring infrastructure specifically, nothing
              else comes close.
            </p>
          </section>

          {/* CTA */}
          <div className="rounded-lg border border-border bg-muted p-8 text-center">
            <Shield className="mx-auto mb-4 h-8 w-8 text-primary" />
            <h2 className="font-sans mb-3 text-xl font-semibold">
              Built on BEAM. Built to stay up.
            </h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              All monitors at 30-second checks, from $3/mo.
              30-day money-back guarantee.
            </p>
            <Button size="lg" asChild>
              <Link to="/pricing">
                See Plans
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </article>
      </>

      </>
  )
}
